import { useState, useCallback } from 'react'
import { Table, Dropdown } from 'antd'

import { ReactComponent as Ellipses } from 'assets/icons/more16.svg'

import Styles from './ManageMembers.module.scss'
import { ManageGuestsProps } from './Types'
import { SettingsGuest } from 'types'
import { IconButton } from 'components/IconButton/IconButton'
import client, { GuestRemovalType } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useRemoveGuestContext } from './RemoveGuestContext'
import { RemoveGuestModal } from 'components/RemoveGuestModal/RemoveGuestModal'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CURRENT_AND_GUEST_COLUMNS, MOBILE_CURRENT_AND_GUEST_COLUMNS, MOBILE_MD_CURRENT_AND_GUEST_COLUMNS, currentAndGuestUserData } from './ManageMembers'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'

const ManageGuests = ({ auth, guests, selectedUser, reloadUsers, setSelectedUser }: ManageGuestsProps) => {
  const [showRemovalModal, setShowRemovalModal] = useState(false)
  const { activeGuest, setActiveGuest, resetGuestContext, fetchStatus, branches } = useRemoveGuestContext()
  const { isMobileMd , isMobile} = useResponsiveDesign()

  const upgradeToMember = useCallback(
    async (guestId: number) => {
      setSelectedUser(null)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const res = await client.upgradeGuestToMember(auth!.company_key, guestId)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        showToastMessage('User was added to workspace', 'success')
        reloadUsers()
      } else {
        showErrorMessageToast('There was a problem upgrading that user to a member. Please try again')
        reportError(new Error('ManageGuests received an error response'), {
          severity: 'warning',
          metaData: { info: `Upgrade to member` },
        })
      }
    },
    [auth, reloadUsers, setSelectedUser],
  )

  const createRemovalCallback = useCallback(
    (removalType: GuestRemovalType) => async () => {
      const guestId = activeGuest?.id
      if (!guestId) return
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const res = await client.removeGuestFromWorkspace(auth!.company_key, guestId, removalType)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        showToastMessage('Guest was removed from the workspace', 'success')
        reloadUsers()
      } else {
        showErrorMessageToast('There was a problem removing that guest from the workspace. Please try again')
        reportError(new Error('ManageGuests received an error response'), {
          severity: 'warning',
          metaData: { info: `Remove guest with removal_type ${removalType}` },
        })
      }
      setShowRemovalModal(false)
      resetGuestContext(guestId)
    },
    [activeGuest?.id, auth, resetGuestContext, reloadUsers],
  )

  const removeGuest = createRemovalCallback(GuestRemovalType.simple)
  const removeGuestAndDeleteBranches = createRemovalCallback(GuestRemovalType.deleteBranches)
  const removeGuestAndTransferOwnership = createRemovalCallback(GuestRemovalType.transferOwnership)

  const actionsMenu = (user: SettingsGuest) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (user.id === auth!.id) return
    const dropdownContent = (
      <div className={Styles.actionsDropdownContent}>
        <div className={Styles.action} onClick={() => upgradeToMember(user.id)} role="button">
          Upgrade to Member
        </div>
        <div className={Styles.divider} />
        <div
          className={Styles.action}
          onClick={() => {
            setSelectedUser(null)
            setShowRemovalModal(true)
          }}
          role="button"
        >
          Remove from Workspace
        </div>
      </div>
    )
    return (
      <Dropdown
        placement="bottomRight"
        overlay={dropdownContent}
        trigger={['click']}
        visible={selectedUser === user}
        onVisibleChange={(open: boolean) => {
          setActiveGuest(user)
          setSelectedUser(open ? user : null)
        }}
      >
        <IconButton className={Styles.moreActions} aria-label="manage user" icon={Ellipses} />
      </Dropdown>
    )
  }

  const columns =
    (isMobileMd && MOBILE_MD_CURRENT_AND_GUEST_COLUMNS) ||
    (isMobile && MOBILE_CURRENT_AND_GUEST_COLUMNS) ||
    CURRENT_AND_GUEST_COLUMNS

  return (
    <div className={Styles.userSection}>
      <div className={Styles.newSectionTitle}>
        <Heading level={5}>Guests</Heading>
        <span className={Styles.sectionTitleCount}>({guests.length || 0})</span>
      </div>
      <Table
        className={Styles.membersTable}
        rowClassName={() => Styles.membersTableRow}
        dataSource={guests.map((user) => ({ ...currentAndGuestUserData(user), actionMenu: actionsMenu(user) }))}
        columns={columns}
        pagination={false}
      />
      <RemoveGuestModal
        visible={showRemovalModal}
        setVisible={setShowRemovalModal}
        onRemove={removeGuest}
        onRemoveAndDelete={removeGuestAndDeleteBranches}
        onRemoveAndTransferOwnership={removeGuestAndTransferOwnership}
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        guestName={activeGuest?.display_name!}
        branches={branches}
        status={fetchStatus}
      />
    </div>
  )
}

export { ManageGuests }
